"use client";

import { useTransition } from "react";
import { useForm } from "react-hook-form";

import * as z from "zod";
import { requestVerificationCode } from "@/app/(portal)/(unauthenticated)/(forms)/actions";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { useToast } from "@/components/ui/hooks/use-toast";
import { InputLabelAnimation as Input } from "@/components/ui/input-label-animation";
import { useLogin } from "@/lib/login/context";
import { zodResolver } from "@hookform/resolvers/zod";

import { EmailLoginFormSchema } from "../schemas";

export default function EmailForm() {
  const { toast } = useToast();
  const [isPending, startTransition] = useTransition();

  const form = useForm<z.infer<typeof EmailLoginFormSchema>>({
    resolver: zodResolver(EmailLoginFormSchema),
    defaultValues: {
      email: "",
    },
  });

  const { setVerification } = useLogin();

  const onSubmit = (data: z.infer<typeof EmailLoginFormSchema>) => {
    startTransition(async () => {
      try {
        await requestVerificationCode("email", data.email);

        setVerification({
          method: "email",
          value: data.email,
        });
        toast({
          title: `A verification code has been sent to ${data.email}`,
        });
      } catch (e) {
        console.error(e);
        toast({
          title: "There was an error sending the verification code",
          variant: "destructive",
        });
      }
    });
  };

  const email = form.watch("email");
  const isReady = EmailLoginFormSchema.shape.email.safeParse(email).success;

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex w-full flex-col gap-6"
      >
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input
                  placeholder="Email Address"
                  type="email"
                  schema={EmailLoginFormSchema}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button
          type="submit"
          className="h-12 w-full rounded-xl px-5 py-3 text-sm font-normal"
          variant={!isReady ? "outline" : "default"}
          disabled={isPending || !isReady}
        >
          Sign In with Email
        </Button>
      </form>
    </Form>
  );
}
